import React, { useState, useEffect } from 'react';
import {
  createBrandGuide,
  generateBrandGuide,
  uploadBrandGuidePDF,
  BrandGuideCreate,
  BrandGuideGenerate,
  previewWebsiteExtraction,
  getBrandGuideTemplates,
  getBrandGuideTemplate,
  createBrandGuideFromTemplate,
  BrandGuideTemplate,
  BrandGuideTemplateDetail
} from '../services/api';
import { GlassCard } from './ui/GlassCard';
import { GlassButton } from './ui/GlassButton';
import { GlassInput } from './ui/GlassInput';
import {
    Laptop,
    Briefcase,
    Heart,
    Palette,
    ShoppingBag,
    Wrench,
    ClipboardList,
    Bot,
    Zap,
    Globe,
    FileText,
    PenLine
} from 'lucide-react';
import './BrandGuideCreator.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


interface BrandGuideCreatorProps {
  projectId: string;
  onClose: () => void;
  onSuccess: () => void;
}

type CreationMethod = 'manual' | 'website' | 'upload' | 'ai' | 'template';

const BrandGuideCreator: React.FC<BrandGuideCreatorProps> = ({
  projectId,
  onClose,
  onSuccess
}) => {
  const [method, setMethod] = useState<CreationMethod | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Templates
  const [templates, setTemplates] = useState<BrandGuideTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<BrandGuideTemplateDetail | null>(null);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  // Form data
  const [brandGuideName, setBrandGuideName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [llmModel, setLlmModel] = useState<'gemini' | 'claude' | 'grok'>('gemini');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [extractionPreview, setExtractionPreview] = useState<any | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Manual form data
  const [manualData, setManualData] = useState<Partial<BrandGuideCreate>>({
    brand_name: '',
    tagline: '',
    mission_statement: '',
    brand_story: '',
    primary_color: '#3498db',
    secondary_color: '#2c3e50',
    accent_color: '#e74c3c',
    primary_font: '',
    secondary_font: '',
    brand_voice: '',
    design_style: ''
  });

  // Load templates on mount
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setIsLoadingTemplates(true);
    try {
      const data = await getBrandGuideTemplates();
      setTemplates(data.templates);
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const handlePreviewWebsite = async () => {
    if (!websiteUrl) {
      setError('Please enter a website URL');
      return;
    }

    setIsLoadingPreview(true);
    setError(null);

    try {
      const preview = await previewWebsiteExtraction(websiteUrl);
      setExtractionPreview(preview);
    } catch (err: any) {
      setError(getApiError(err, 'Failed to preview website extraction'));
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleGenerateFromWebsite = async () => {
    if (!brandGuideName || !websiteUrl) {
      setError('Please provide a brand guide name and website URL');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const data: BrandGuideGenerate = {
        project_id: projectId,
        brand_guide_name: brandGuideName,
        source_type: 'website',
        source_url: websiteUrl,
        custom_prompt: customPrompt || undefined,
        llm_model: llmModel
      };

      await generateBrandGuide(data);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(getApiError(err, 'Failed to generate brand guide'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateFromAI = async () => {
    if (!brandGuideName || !customPrompt) {
      setError('Please provide a brand guide name and description');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const data: BrandGuideGenerate = {
        project_id: projectId,
        brand_guide_name: brandGuideName,
        source_type: 'manual_prompt',
        custom_prompt: customPrompt,
        llm_model: llmModel
      };

      await generateBrandGuide(data);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(getApiError(err, 'Failed to generate brand guide'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUploadPDF = async () => {
    if (!brandGuideName || !selectedFile) {
      setError('Please provide a brand guide name and select a PDF file');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      await uploadBrandGuidePDF(projectId, brandGuideName, selectedFile, llmModel);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(getApiError(err, 'Failed to upload and process PDF'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCreateManual = async () => {
    if (!brandGuideName) {
      setError('Please provide a brand guide name');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const data: BrandGuideCreate = {
        project_id: projectId,
        brand_guide_name: brandGuideName,
        ...manualData,
        source_type: 'manual'
      };

      await createBrandGuide(data);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(getApiError(err, 'Failed to create brand guide'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCreateFromTemplate = async (templateId: string) => {
    if (!brandGuideName) {
      setError('Please provide a brand guide name');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      // Allow customizing brand_name if provided
      const customizations = manualData.brand_name ? { brand_name: manualData.brand_name } : undefined;

      await createBrandGuideFromTemplate(projectId, brandGuideName, templateId, customizations);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(getApiError(err, 'Failed to create brand guide from template'));
    } finally {
      setIsGenerating(false);
    }
  };

  const renderMethodSelection = () => (
    <GlassCard variant="default" padding="lg">
      <div className="method-selection">
        <h2 style={{ margin: '0 0 8px 0', fontSize: '22px', fontWeight: 300 }}>Create Brand Guide</h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
          Choose how you'd like to create your brand guide:
        </p>

        <div className="methods-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          <GlassCard variant="hover" padding="md" onClick={() => setMethod('template')}>
            <div className="method-icon"><Zap size={32} /></div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>From Template</h3>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
              Start with an industry-specific template
            </p>
          </GlassCard>

          <GlassCard variant="hover" padding="md" onClick={() => setMethod('website')}>
            <div className="method-icon"><Globe size={32} /></div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>From Website</h3>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
              Extract brand elements from an existing website
            </p>
          </GlassCard>

          <GlassCard variant="hover" padding="md" onClick={() => setMethod('upload')}>
            <div className="method-icon"><FileText size={32} /></div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>Upload PDF</h3>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
              Upload a brand guidelines PDF document
            </p>
          </GlassCard>

          <GlassCard variant="hover" padding="md" onClick={() => setMethod('ai')}>
            <div className="method-icon"><Bot size={32} /></div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>AI Generation</h3>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
              Describe your brand and let AI create the guide
            </p>
          </GlassCard>

          <GlassCard variant="hover" padding="md" onClick={() => setMethod('manual')}>
            <div className="method-icon"><PenLine size={32} /></div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>Manual Entry</h3>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
              Manually enter all brand details
            </p>
          </GlassCard>
        </div>

        <GlassButton variant="ghost" onClick={onClose}>Cancel</GlassButton>
      </div>
    </GlassCard>
  );

  const renderWebsiteForm = () => (
    <GlassCard variant="default" padding="lg">
      <div className="creation-form">
        <h2 style={{ margin: '0 0 24px 0', fontSize: '22px', fontWeight: 300 }}>Generate from Website</h2>

        <div className="form-group" style={{ marginBottom: '20px' }}>
          <GlassInput
            label="Brand Guide Name *"
            value={brandGuideName}
            onChange={setBrandGuideName}
            placeholder="e.g., Summer Campaign Brand, Corporate Brand"
          />
        </div>

        <div className="form-group" style={{ marginBottom: '20px' }}>
          <GlassInput
            label="Website URL *"
            type="url"
            value={websiteUrl}
            onChange={setWebsiteUrl}
            placeholder="https://example.com"
          />
          <div style={{ marginTop: '12px' }}>
            <GlassButton
              variant="secondary"
              onClick={handlePreviewWebsite}
              disabled={isLoadingPreview || !websiteUrl}
              size="sm"
            >
              {isLoadingPreview ? 'Loading...' : 'Preview Extraction'}
            </GlassButton>
          </div>
        </div>

        {extractionPreview && (
          <GlassCard variant="strong" padding="md" className="extraction-preview" style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px' }}>Extracted Elements:</h4>
            <div className="preview-content" style={{ fontSize: '14px' }}>
              {extractionPreview.extracted_data?.colors && (
                <div style={{ marginBottom: '8px' }}>
                  <strong>Colors:</strong> {extractionPreview.extracted_data.colors.join(', ')}
                </div>
              )}
              {extractionPreview.extracted_data?.fonts && (
                <div>
                  <strong>Fonts:</strong> {extractionPreview.extracted_data.fonts.join(', ')}
                </div>
              )}
            </div>
          </GlassCard>
        )}

        <div className="form-group" style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
            Additional Instructions (Optional)
          </label>
          <textarea
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            placeholder="Any specific brand elements to focus on..."
            rows={3}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '14px',
              fontFamily: 'inherit',
              color: 'var(--text)',
              background: 'var(--panel)',
              border: '1px solid var(--stroke)',
              borderRadius: 'var(--radius-md)',
              backdropFilter: 'blur(20px)',
              resize: 'vertical',
              outline: 'none',
            }}
          />
        </div>

        <div className="form-group" style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
            AI Model
          </label>
          <select
            value={llmModel}
            onChange={(e) => setLlmModel(e.target.value as any)}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '14px',
              color: 'var(--text)',
              background: 'var(--panel)',
              border: '1px solid var(--stroke)',
              borderRadius: 'var(--radius-md)',
              backdropFilter: 'blur(20px)',
              cursor: 'pointer',
            }}
          >
            <option value="gemini">Gemini (Recommended)</option>
            <option value="claude">Claude</option>
            <option value="grok">Grok</option>
          </select>
        </div>

        {error && (
          <div className="error-message" style={{
            padding: '12px 16px',
            background: 'rgba(248, 113, 113, 0.1)',
            border: '1px solid var(--danger)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--danger)',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <div className="form-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <GlassButton variant="ghost" onClick={() => setMethod(null)} disabled={isGenerating}>
            Back
          </GlassButton>
          <GlassButton variant="primary" onClick={handleGenerateFromWebsite} disabled={isGenerating} loading={isGenerating}>
            Generate Brand Guide
          </GlassButton>
        </div>
      </div>
    </GlassCard>
  );

  const renderUploadForm = () => (
    <GlassCard variant="default" padding="lg">
      <div className="creation-form">
        <h2 style={{ margin: '0 0 24px 0', fontSize: '22px', fontWeight: 300 }}>Upload Brand Guidelines PDF</h2>

        <div className="form-group" style={{ marginBottom: '20px' }}>
          <GlassInput
            label="Brand Guide Name *"
            value={brandGuideName}
            onChange={setBrandGuideName}
            placeholder="e.g., Summer Campaign Brand"
          />
        </div>

        <div className="form-group" style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
            PDF File *
          </label>
          <input
            type="file"
            accept=".pdf"
            onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '14px',
              color: 'var(--text)',
              background: 'var(--panel)',
              border: '1px solid var(--stroke)',
              borderRadius: 'var(--radius-md)',
              backdropFilter: 'blur(20px)',
              cursor: 'pointer',
            }}
          />
          {selectedFile && (
            <p className="file-info" style={{ marginTop: '8px', fontSize: '13px', color: 'var(--text-secondary)' }}>
              Selected: {selectedFile.name}
            </p>
          )}
        </div>

        <div className="form-group" style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
            AI Model
          </label>
          <select
            value={llmModel}
            onChange={(e) => setLlmModel(e.target.value as any)}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '14px',
              color: 'var(--text)',
              background: 'var(--panel)',
              border: '1px solid var(--stroke)',
              borderRadius: 'var(--radius-md)',
              backdropFilter: 'blur(20px)',
              cursor: 'pointer',
            }}
          >
            <option value="gemini">Gemini (Recommended)</option>
            <option value="claude">Claude</option>
            <option value="grok">Grok</option>
          </select>
        </div>

        {error && (
          <div className="error-message" style={{
            padding: '12px 16px',
            background: 'rgba(248, 113, 113, 0.1)',
            border: '1px solid var(--danger)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--danger)',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <div className="form-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <GlassButton variant="ghost" onClick={() => setMethod(null)} disabled={isGenerating}>
            Back
          </GlassButton>
          <GlassButton variant="primary" onClick={handleUploadPDF} disabled={isGenerating} loading={isGenerating}>
            Upload & Process
          </GlassButton>
        </div>
      </div>
    </GlassCard>
  );

  const renderAIForm = () => (
    <GlassCard variant="default" padding="lg">
      <div className="creation-form">
        <h2 style={{ margin: '0 0 24px 0', fontSize: '22px', fontWeight: 300 }}>AI-Generated Brand Guide</h2>

        <div className="form-group" style={{ marginBottom: '20px' }}>
          <GlassInput
            label="Brand Guide Name *"
            value={brandGuideName}
            onChange={setBrandGuideName}
            placeholder="e.g., Tech Startup Brand"
          />
        </div>

        <div className="form-group" style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
            Brand Description *
          </label>
          <textarea
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            placeholder="Describe your brand, values, target audience, industry, and any specific requirements..."
            rows={8}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '14px',
              fontFamily: 'inherit',
              color: 'var(--text)',
              background: 'var(--panel)',
              border: '1px solid var(--stroke)',
              borderRadius: 'var(--radius-md)',
              backdropFilter: 'blur(20px)',
              resize: 'vertical',
              outline: 'none',
            }}
          />
          <small style={{ display: 'block', marginTop: '8px', fontSize: '12px', color: 'var(--text-muted)' }}>
            Be specific! Include details about your industry, target audience, brand personality, preferred colors/styles, etc.
          </small>
        </div>

        <div className="form-group" style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
            AI Model
          </label>
          <select
            value={llmModel}
            onChange={(e) => setLlmModel(e.target.value as any)}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '14px',
              color: 'var(--text)',
              background: 'var(--panel)',
              border: '1px solid var(--stroke)',
              borderRadius: 'var(--radius-md)',
              backdropFilter: 'blur(20px)',
              cursor: 'pointer',
            }}
          >
            <option value="gemini">Gemini (Recommended)</option>
            <option value="claude">Claude</option>
            <option value="grok">Grok</option>
          </select>
        </div>

        {error && (
          <div className="error-message" style={{
            padding: '12px 16px',
            background: 'rgba(248, 113, 113, 0.1)',
            border: '1px solid var(--danger)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--danger)',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <div className="form-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <GlassButton variant="ghost" onClick={() => setMethod(null)} disabled={isGenerating}>
            Back
          </GlassButton>
          <GlassButton variant="primary" onClick={handleGenerateFromAI} disabled={isGenerating} loading={isGenerating}>
            Generate Brand Guide
          </GlassButton>
        </div>
      </div>
    </GlassCard>
  );

  const renderManualForm = () => (
    <GlassCard variant="default" padding="lg">
      <div className="creation-form manual-form">
        <h2 style={{ margin: '0 0 24px 0', fontSize: '22px', fontWeight: 300 }}>Manual Brand Guide</h2>

        <div className="form-group" style={{ marginBottom: '20px' }}>
          <GlassInput
            label="Brand Guide Name *"
            value={brandGuideName}
            onChange={setBrandGuideName}
            placeholder="e.g., Summer Campaign Brand"
          />
        </div>

        <GlassCard variant="strong" padding="md" style={{ marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Brand Identity</h3>
          <div className="form-group" style={{ marginBottom: '16px' }}>
            <GlassInput
              label="Brand Name"
              value={manualData.brand_name || ''}
              onChange={(val) => setManualData({...manualData, brand_name: val})}
              placeholder="Your Brand Name"
            />
          </div>

          <div className="form-group" style={{ marginBottom: '16px' }}>
            <GlassInput
              label="Tagline"
              value={manualData.tagline || ''}
              onChange={(val) => setManualData({...manualData, tagline: val})}
              placeholder="Your Brand Tagline"
            />
          </div>

          <div className="form-group">
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
              Mission Statement
            </label>
            <textarea
              value={manualData.mission_statement || ''}
              onChange={(e) => setManualData({...manualData, mission_statement: e.target.value})}
              rows={3}
              placeholder="What is your brand's mission?"
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '14px',
                fontFamily: 'inherit',
                color: 'var(--text)',
                background: 'var(--panel)',
                border: '1px solid var(--stroke)',
                borderRadius: 'var(--radius-md)',
                backdropFilter: 'blur(20px)',
                resize: 'vertical',
                outline: 'none',
              }}
            />
          </div>
        </GlassCard>

        <GlassCard variant="strong" padding="md" style={{ marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Colors</h3>
          <div className="color-inputs" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                Primary Color
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <input
                  type="color"
                  value={manualData.primary_color || '#3498db'}
                  onChange={(e) => setManualData({...manualData, primary_color: e.target.value})}
                  style={{
                    width: '48px',
                    height: '48px',
                    border: '1px solid var(--stroke)',
                    borderRadius: 'var(--radius-md)',
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                />
                <span style={{ fontSize: '14px', fontFamily: 'monospace', color: 'var(--text-secondary)' }}>
                  {manualData.primary_color}
                </span>
              </div>
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                Secondary Color
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <input
                  type="color"
                  value={manualData.secondary_color || '#2c3e50'}
                  onChange={(e) => setManualData({...manualData, secondary_color: e.target.value})}
                  style={{
                    width: '48px',
                    height: '48px',
                    border: '1px solid var(--stroke)',
                    borderRadius: 'var(--radius-md)',
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                />
                <span style={{ fontSize: '14px', fontFamily: 'monospace', color: 'var(--text-secondary)' }}>
                  {manualData.secondary_color}
                </span>
              </div>
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                Accent Color
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <input
                  type="color"
                  value={manualData.accent_color || '#e74c3c'}
                  onChange={(e) => setManualData({...manualData, accent_color: e.target.value})}
                  style={{
                    width: '48px',
                    height: '48px',
                    border: '1px solid var(--stroke)',
                    borderRadius: 'var(--radius-md)',
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                />
                <span style={{ fontSize: '14px', fontFamily: 'monospace', color: 'var(--text-secondary)' }}>
                  {manualData.accent_color}
                </span>
              </div>
            </div>
          </div>
        </GlassCard>

        <GlassCard variant="strong" padding="md" style={{ marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Typography</h3>
          <div className="form-group" style={{ marginBottom: '16px' }}>
            <GlassInput
              label="Primary Font (Headings)"
              value={manualData.primary_font || ''}
              onChange={(val) => setManualData({...manualData, primary_font: val})}
              placeholder="e.g., Montserrat, Roboto"
            />
          </div>

          <div className="form-group">
            <GlassInput
              label="Secondary Font (Body Text)"
              value={manualData.secondary_font || ''}
              onChange={(val) => setManualData({...manualData, secondary_font: val})}
              placeholder="e.g., Open Sans, Lato"
            />
          </div>
        </GlassCard>

        <GlassCard variant="strong" padding="md" style={{ marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Voice & Style</h3>
          <div className="form-group" style={{ marginBottom: '16px' }}>
            <GlassInput
              label="Brand Voice"
              value={manualData.brand_voice || ''}
              onChange={(val) => setManualData({...manualData, brand_voice: val})}
              placeholder="e.g., Professional, Friendly, Authoritative"
            />
          </div>

          <div className="form-group">
            <GlassInput
              label="Design Style"
              value={manualData.design_style || ''}
              onChange={(val) => setManualData({...manualData, design_style: val})}
              placeholder="e.g., Modern, Minimal, Bold, Elegant"
            />
          </div>
        </GlassCard>

        {error && (
          <div className="error-message" style={{
            padding: '12px 16px',
            background: 'rgba(248, 113, 113, 0.1)',
            border: '1px solid var(--danger)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--danger)',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <div className="form-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <GlassButton variant="ghost" onClick={() => setMethod(null)} disabled={isGenerating}>
            Back
          </GlassButton>
          <GlassButton variant="primary" onClick={handleCreateManual} disabled={isGenerating} loading={isGenerating}>
            Create Brand Guide
          </GlassButton>
        </div>
      </div>
    </GlassCard>
  );

  const renderTemplateForm = () => (
    <GlassCard variant="default" padding="lg">
      <div className="creation-form">
        <h2 style={{ margin: '0 0 24px 0', fontSize: '22px', fontWeight: 300 }}>Choose a Template</h2>

        <div className="form-group" style={{ marginBottom: '20px' }}>
          <GlassInput
            label="Brand Guide Name *"
            value={brandGuideName}
            onChange={setBrandGuideName}
            placeholder="e.g., Summer Campaign Brand"
          />
        </div>

        <div className="form-group" style={{ marginBottom: '20px' }}>
          <GlassInput
            label="Your Brand Name (Optional)"
            value={manualData.brand_name || ''}
            onChange={(val) => setManualData({...manualData, brand_name: val})}
            placeholder="Your Company Name"
          />
          <small style={{ display: 'block', marginTop: '8px', fontSize: '12px', color: 'var(--text-muted)' }}>
            This will override the template's default brand name
          </small>
        </div>

        {isLoadingTemplates ? (
          <div className="templates-loading" style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: 'var(--text-secondary)' }}>Loading templates...</p>
          </div>
        ) : (
          <div className="templates-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            {templates.map((template) => (
              <GlassCard
                key={template.template_id}
                variant={selectedTemplate?.template_id === template.template_id ? 'strong' : 'hover'}
                padding="md"
                onClick={async () => {
                  try {
                    const fullTemplate = await getBrandGuideTemplate(template.template_id);
                    setSelectedTemplate(fullTemplate);
                  } catch (err) {
                    console.error('Failed to load template details:', err);
                  }
                }}
              >
                <div className="method-icon">
                  {getCategoryIcon(template.category)}
                </div>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>{template.template_name}</h3>
                <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
                  {template.template_description}
                </p>
                {selectedTemplate?.template_id === template.template_id && (
                  <div className="template-preview" style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--stroke)' }}>
                    <div className="color-preview" style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                      <span
                        className="color-dot"
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          backgroundColor: selectedTemplate.brand_data.primary_color,
                          border: '2px solid var(--stroke)'
                        }}
                      ></span>
                      <span
                        className="color-dot"
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          backgroundColor: selectedTemplate.brand_data.secondary_color,
                          border: '2px solid var(--stroke)'
                        }}
                      ></span>
                      <span
                        className="color-dot"
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          backgroundColor: selectedTemplate.brand_data.accent_color,
                          border: '2px solid var(--stroke)'
                        }}
                      ></span>
                    </div>
                    <small style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      <strong>Voice:</strong> {selectedTemplate.brand_data.brand_voice}
                    </small>
                  </div>
                )}
              </GlassCard>
            ))}
          </div>
        )}

        {error && (
          <div className="error-message" style={{
            padding: '12px 16px',
            background: 'rgba(248, 113, 113, 0.1)',
            border: '1px solid var(--danger)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--danger)',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <div className="form-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <GlassButton variant="ghost" onClick={() => setMethod(null)} disabled={isGenerating}>
            Back
          </GlassButton>
          <GlassButton
            variant="primary"
            onClick={() => selectedTemplate && handleCreateFromTemplate(selectedTemplate.template_id)}
            disabled={isGenerating || !selectedTemplate || !brandGuideName}
            loading={isGenerating}
          >
            Create from Template
          </GlassButton>
        </div>
      </div>
    </GlassCard>
  );

  const getCategoryIcon = (category: string): React.ReactNode => {
    const iconProps = { size: 32 };
    switch (category) {
      case 'Technology': return <Laptop {...iconProps} />;
      case 'Professional Services': return <Briefcase {...iconProps} />;
      case 'Healthcare': return <Heart {...iconProps} />;
      case 'Creative': return <Palette {...iconProps} />;
      case 'Retail': return <ShoppingBag {...iconProps} />;
      case 'Trades': return <Wrench {...iconProps} />;
      default: return <ClipboardList {...iconProps} />;
    }
  };

  return (
    <div className="brand-guide-creator-overlay">
      <div className="brand-guide-creator" style={{ width: '100%', maxWidth: '800px' }}>
        {!method && renderMethodSelection()}
        {method === 'template' && renderTemplateForm()}
        {method === 'website' && renderWebsiteForm()}
        {method === 'upload' && renderUploadForm()}
        {method === 'ai' && renderAIForm()}
        {method === 'manual' && renderManualForm()}
      </div>
    </div>
  );
};

export default BrandGuideCreator;
